import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AddLink from "@mui/icons-material/AddLink";
import AutoAwesome from "@mui/icons-material/AutoAwesome";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import DoneAllRounded from "@mui/icons-material/DoneAllRounded";
import EditOutlined from "@mui/icons-material/EditOutlined";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import HomeWorkOutlined from "@mui/icons-material/HomeWorkOutlined";
import ListAltIcon from "@mui/icons-material/ListAlt";
import LocalOfferOutlined from "@mui/icons-material/LocalOfferOutlined";
import LocationCityOutlined from "@mui/icons-material/LocationCityOutlined";
import MarkunreadMailboxOutlined from "@mui/icons-material/MarkunreadMailboxOutlined";
import ModeStandby from "@mui/icons-material/ModeStandby";
import PauseCircleOutlineRounded from "@mui/icons-material/PauseCircleOutlineRounded";
import People from "@mui/icons-material/People";
import PersonOutlineRounded from "@mui/icons-material/PersonOutlineRounded";
import PlaceRounded from "@mui/icons-material/PlaceRounded";
import SettingsRounded from "@mui/icons-material/SettingsRounded";
import SocialDistance from "@mui/icons-material/SocialDistance";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { useSelector, useDispatch } from "react-redux";
import { showToast } from "services/utils/Status";
import { Loader } from "components/Loader";
import { siteSettingsEditToastMessage } from "services/constants";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { tooltipStyleProps } from "services/constants/Styles";
import sessionHandling from "services/utils/notificationUtils";
import { setFilterApi } from "services/Redux/userManagament";
import {
  StatCard,
  designColors,
  designShell,
  paginationFooter,
  rowsPerPageOptions,
} from "services/constants/designTokens";
import {
  GlobalConfirmDialog,
  sharedStickyCustomFilterBarSx,
  sharedStickyFooterBarSx,
} from "components/DesignSystem/sharedUiPatterns";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { SiteLocationMap } from "components/DesignSystem/SiteLocationMap";
import {
  RequestDetailField,
  detailAccentPalette,
  detailCardSx,
  detailSectionLabelSx,
} from "components/DesignSystem/RequestDetailField";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { FilterProvider } from "pages/FilterContext";
import { ApproveTable } from "pages/TeamZone/Approve/approveUi";
import {
  balancedColumnWidth,
  monitoringBodyCellSx,
  monitoringHeadCellSx,
  monitoringNameSx,
  monitoringRowSx,
  useMeasuredHeight,
} from "pages/Monitoring/listUi";
import { SiteEditPanel } from "./";
import { PanelIconHeader, PanelSectionCard, useFilteredRows } from "../settingsUi";
import { resolveSettingsAccess } from "../settingsAccess";

// Standard list headers: drive the table columns, the CustomFilter toolbar
// (search / sort / filter / download) and the "Default View" column picker.
// A row opens its site in the details panel; there is no Action column.
const siteHeaders = [
  { name: "Site Name", key: "location_name", type: "string", icon: <ModeStandby fontSize="small" /> },
  { name: "Address", key: "address", type: "string", icon: <HomeWorkOutlined fontSize="small" /> },
  { name: "Location", key: "city", type: "string", icon: <PlaceRounded fontSize="small" /> },
  { name: "Radius (m)", key: "radius", type: "number", icon: <SocialDistance fontSize="small" /> },
  { name: "Tag", key: "tag", type: "autocomplete", icon: <AutoAwesome fontSize="small" /> },
  { name: "Added by", key: "username", type: "string", icon: <People fontSize="small" /> },
  { name: "Site Status", key: "location_status", type: "autocomplete", icon: <AddLink fontSize="small" /> },
];

// Fixed-layout widths; "Address" takes whatever is left so the table always
// fits its card.
const columnWidths = {
  location_name: "17%",
  city: "15%",
  radius: "10%",
  tag: "10%",
  username: "16%",
  location_status: "12%",
};

// Same accent/tint per status as the Team Workspace Approvals status cards
// (Approve/Leave/LeaveStatusCards.js), so "Pending" reads the same colour
// everywhere. Hold and Completed only exist for sites.
const STATUS_CARD_DEFS = [
  { key: "Pending", label: "Pending", icon: AccessTimeIcon, accent: "#F97316", tint: "#FFF7ED" },
  { key: "Approved", label: "Approved", icon: CheckCircleOutlineIcon, accent: "#16A34A", tint: "#EAFBF1" },
  { key: "Rejected", label: "Rejected", icon: HighlightOffIcon, accent: "#EF4444", tint: "#FEF2F2" },
  { key: "Hold", label: "Hold", icon: PauseCircleOutlineRounded, accent: "#2563EB", tint: "#EFF6FF" },
  { key: "Completed", label: "Completed", icon: TaskAltIcon, accent: "#0F766E", tint: "#E6F8F4" },
];
const ALL_CARD_DEF = { key: "All", label: "All", icon: ListAltIcon, accent: "#1D4ED8", tint: "#EEF4FF" };
// List and panel chips take their colour from the cards.
const STATUS_COLORS = Object.fromEntries(
  STATUS_CARD_DEFS.map((card) => [card.key, card.accent]),
);
const TAG_COLORS = { Home: "#2563EB", Office: "#6D5BD0", Site: "#F97316" };
const NEUTRAL_COLOR = "#6B7280";

// The status changes offered for a site, by its current status.
const ACTIONS_BY_STATUS = {
  Approved: ["Hold", "Reject", "Complete"],
  Pending: ["Approve", "Reject", "Complete"],
  Hold: ["Approve", "Reject", "Complete"],
  Completed: ["Reject"],
  Rejected: ["Approve", "Hold", "Delete"],
};

const ACTION_META = {
  Approve: { label: "Approve Site", icon: CheckCircleRounded },
  Hold: { label: "Put on Hold", icon: PauseCircleOutlineRounded },
  Reject: { label: "Reject Site", icon: CloseRounded, danger: true },
  Complete: { label: "Mark as Completed", icon: DoneAllRounded },
  Delete: { label: "Delete Site", icon: DeleteOutlineRounded, danger: true },
};

const DENIED_MESSAGES = {
  Edit: "Access denied: You are not authorized to edit this location detail",
  Hold: "Access denied: You are not authorized to onhold this location detail",
  Reject: "Access denied: You are not authorized to reject this location detail",
  Complete:
    "Access denied: You are not authorized to complete this location detail",
  Approve:
    "Access denied: You are not authorized to approve this location detail",
  Delete: "Access denied: You are not authorized to delete this location detail",
};

const statusAfter = (option) =>
  option === "Approve" || option === "Complete"
    ? option + "d"
    : option === "Reject"
      ? option + "ed"
      : option;

const initials = (name) =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

const openInMaps = (site) =>
  window.open(
    `https://www.google.com/maps?q=${site.latitude},${site.longitude}&z=15`,
    "_blank",
  );

const UserAvatar = ({ user, size = 28 }) => (
  <Mui.Avatar
    src={user?.image_url || undefined}
    alt=""
    sx={{
      width: size,
      height: size,
      fontSize: size * 0.42,
      fontWeight: 600,
      bgcolor: designColors.tealTint,
      color: designColors.tealDark,
      flexShrink: 0,
    }}
  >
    {initials(user?.username)}
  </Mui.Avatar>
);

// Map of the site with its geo-fence radius (shared with Administration > Sites).
// The label is the place the site is in ("Location" in the details above).
const SiteMap = ({ site }) => (
  <SiteLocationMap
    latitude={site.latitude}
    longitude={site.longitude}
    radius={site.radius}
    label={site.city || site.location_name}
  />
);

const actionButtonSx = (danger) => ({
  justifyContent: "flex-start",
  minHeight: 42,
  px: 1.5,
  borderRadius: "8px",
  textTransform: "none",
  fontSize: 14,
  fontWeight: 500,
  color: danger ? "#DC2626" : designColors.slate700,
  borderColor: danger ? "#FCA5A5" : designColors.slate200,
  backgroundColor: danger ? "#FEF2F2" : designColors.slateBg,
  "&:hover": {
    borderColor: danger ? "#DC2626" : designColors.slate300,
    backgroundColor: danger ? "#FEE2E2" : designColors.slate100,
  },
});

export const SiteSettings = ({ moduleDetails: moduleDetailsProp }) => {
  const { domain, token, userScreenAccess, userDetails } = useSelector(
    (state) => state.tokenReducer,
  );
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [update, setUpdate] = useState(true);
  const [sites, setSites] = useState([]);
  // The rows after the toolbar's search, filters and sort (see
  // useFilteredRows for why a stale result is never shown).
  const {
    rows: filteredRows,
    ready: filteredReady,
    onFilteredDataChange,
  } = useFilteredRows(sites);
  const [statusFilter, setStatusFilter] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [viewId, setViewId] = useState(null);
  const [editSite, setEditSite] = useState(null);
  const [siteToDelete, setSiteToDelete] = useState(null);
  const [busyAction, setBusyAction] = useState(null);
  const [customFilterRef, customFilterHeight] = useMeasuredHeight();
  const [footerRef, footerHeight] = useMeasuredHeight();
  const resetPage = useCallback(() => setPage(0), []);

  // Opened from Team Workspace > Settings, where Redux holds Team Workspace's
  // provisioning payload rather than the one that owns this screen's
  // permissions; the page passes the right module list in (see
  // TeamZone/Settings/settingsAccess.js).
  const moduleDetails =
    moduleDetailsProp ?? userScreenAccess?.module_details;

  // People's real provisioning payload names this module "Settings", not
  // "Feature Configuration" (see Sidebar/index.js's PEOPLE_VIEWER_GATED_MODULES
  // comment).
  const FeatureConfig = moduleDetails?.find(
    (mod) => mod.module_name === "Settings",
  );
  const subModuleId = FeatureConfig?.submodules?.find(
    (sub) => sub.sub_module_name === "Geo Fenced Sites",
  )?.sub_module_id;

  const check_access = checkEditorPermission(
    moduleDetails,
    "Settings",
    "Geo Fenced Sites",
  );
  // One permission for the whole screen ("Settings" > "Geo Fenced Sites"):
  // editor edits a site; approver changes its status (approve, hold, reject,
  // complete) and deletes it. These used to be gated on a different module
  // ("LMS" > "Site Settings"), which a Team Workspace payload never carries.
  const { canDelete: canApprove } = resolveSettingsAccess(
    moduleDetails,
    "Geo Fenced Sites",
  );
  const canRun = (option) => (option === "Edit" ? check_access : canApprove);

  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "team_settings_geo_fenced_sites_saved_views",
    fullHeaders: siteHeaders,
    screenAccess: Boolean(subModuleId),
    onViewChange: resetPage,
  });

  useEffect(() => {
    const getData = async () => {
      try {
        const response = await fetch(`${domain}location_details/`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${token}`,
          },
        });
        const res = await response.json();
        if (response.ok) {
          setSites(
            res.map((obj) => ({
              ...obj,
              username: obj.users?.[0]?.username || "",
            })),
          );
          dispatch(setFilterApi(true));
        } else if (response.status === 409) {
          sessionHandling();
        } else {
          throw new Error("REQUEST_FAILED");
        }
      } catch (error) {
        console.error("[Sitesettings] operation failed:", error);
        showToast("error", "Couldn't load site details. Please refresh the page.");
      } finally {
        setIsLoading(false);
      }
    };
    if (update) {
      getData();
      setUpdate(false);
    }
  }, [dispatch, domain, token, update]);

  // Same wiring as the Approvals lists: the cards count what CustomFilter
  // produced (search / filters applied), and the selected card is layered on
  // top of that set.
  const countByStatus = useMemo(() => {
    const counts = {};
    filteredRows.forEach((site) => {
      counts[site.location_status] = (counts[site.location_status] || 0) + 1;
    });
    return counts;
  }, [filteredRows]);

  const rows = useMemo(
    () =>
      statusFilter
        ? filteredRows.filter((site) => site.location_status === statusFilter)
        : filteredRows,
    [filteredRows, statusFilter],
  );
  const safePage = Math.min(
    page,
    Math.max(0, Math.ceil(rows.length / rowsPerPage) - 1),
  );
  const pageStart = safePage * rowsPerPage;
  const viewing = sites.find((site) => site.id === viewId);

  const updateStatus = async (site, option) => {
    setBusyAction(option);
    try {
      const response = await fetch(`${domain}edit_location/`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `token ${token}`,
        },
        body: JSON.stringify({
          location_id: site.id,
          location_status: statusAfter(option),
          city: site.city,
          location_name: site.location_name,
          address: site.address,
          tag: site.tag,
          pincode: site.pincode,
        }),
      });
      await response.json();
      if (response.ok) {
        showToast("success", siteSettingsEditToastMessage.editMessage);
        setSites((previous) =>
          previous.map((element) =>
            element.id === site.id
              ? { ...element, location_status: statusAfter(option) }
              : element,
          ),
        );
        setUpdate(true);
      } else if (response.status === 409) {
        sessionHandling();
      } else {
        throw new Error("REQUEST_FAILED");
      }
    } catch (error) {
      console.error("[Sitesettings] operation failed:", error);
      showToast("error", "Couldn't update the site status. Please try again.");
    } finally {
      setBusyAction(null);
    }
  };

  const deleteLocation = async () => {
    setBusyAction("Delete");
    try {
      const response = await fetch(`${domain}location_details/`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `token ${token}`,
        },
        body: JSON.stringify({
          location_id: siteToDelete.id,
          isdeleted: true,
        }),
      });
      await response.json();
      if (response.ok) {
        showToast("success", "Location deleted.");
        if (siteToDelete.id === viewId) setViewId(null);
        setUpdate(true);
      } else if (response.status === 409) {
        sessionHandling();
      } else {
        throw new Error("REQUEST_FAILED");
      }
    } catch (error) {
      console.error("[Sitesettings] operation failed:", error);
      showToast("error", "Couldn't delete the site. Please try again.");
    } finally {
      setBusyAction(null);
      setSiteToDelete(null);
    }
  };

  const runAction = (site, option) => {
    if (!canRun(option)) {
      showToast("error", DENIED_MESSAGES[option]);
      return;
    }
    if (option === "Edit") setEditSite(site);
    else if (option === "Delete") setSiteToDelete(site);
    else updateStatus(site, option);
  };

  const renderCell = (key, site) => {
    switch (key) {
      case "location_name":
        return (
          <Mui.Typography noWrap sx={monitoringNameSx(viewId === site.id)}>
            {site.location_name}
          </Mui.Typography>
        );
      case "city":
        return (
          <Mui.Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Mui.Tooltip
              arrow
              slotProps={tooltipStyleProps}
              title={site.latitude + " - " + site.longitude}
            >
              <Mui.IconButton
                size="small"
                aria-label={"Open " + site.location_name + " in Google Maps"}
                onClick={(event) => {
                  event.stopPropagation();
                  openInMaps(site);
                }}
                sx={{ p: 0.25, color: designColors.slate600 }}
              >
                <PlaceRounded fontSize="small" />
              </Mui.IconButton>
            </Mui.Tooltip>
            <Mui.Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {site.city || "—"}
            </Mui.Box>
          </Mui.Stack>
        );
      case "radius":
        return Number.isFinite(parseInt(site.radius, 10))
          ? parseInt(site.radius, 10)
          : "—";
      case "tag":
        return site.tag ? (
          <RequestStatusChip status={site.tag} colorCode={TAG_COLORS[site.tag] || NEUTRAL_COLOR} />
        ) : (
          "—"
        );
      case "username":
        return (
          <Mui.Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <UserAvatar user={site.users?.[0]} />
            <Mui.Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {site.username || "—"}
            </Mui.Box>
          </Mui.Stack>
        );
      case "location_status":
        return (
          <RequestStatusChip
            colorCode={STATUS_COLORS[site.location_status] || NEUTRAL_COLOR}
            status={site.location_status}
          />
        );
      default:
        return site[key] || "—";
    }
  };

  const viewFields = viewing
    ? [
        { icon: HomeWorkOutlined, label: "Address", value: viewing.address, accent: detailAccentPalette.indigo, multiline: true },
        { icon: LocationCityOutlined, label: "Location", value: viewing.city, accent: detailAccentPalette.blue },
        { icon: MarkunreadMailboxOutlined, label: "Pincode", value: viewing.pincode, accent: detailAccentPalette.pink },
        {
          icon: SocialDistance,
          label: "Radius",
          value: Number.isFinite(parseInt(viewing.radius, 10))
            ? parseInt(viewing.radius, 10) + " metres"
            : "",
          accent: detailAccentPalette.green,
        },
        { icon: LocalOfferOutlined, label: "Tag", value: viewing.tag, accent: detailAccentPalette.orange },
        { icon: PersonOutlineRounded, label: "Added by", value: viewing.username, accent: detailAccentPalette.purple },
      ]
    : [];
  // Actions the user may not run are not offered at all.
  const viewActions = viewing
    ? (ACTIONS_BY_STATUS[viewing.location_status] || []).filter(canRun)
    : [];

  return (
    <>
      <FilterProvider
        domain={domain}
        userDetails={userDetails}
        subModuleId={subModuleId}
      >
        {/* Same grid and card style as the Approvals status cards. */}
        <Mui.Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            "@media (max-width:599.95px)": {
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              "& > :last-child:nth-child(odd)": { gridColumn: "1 / -1" },
            },
            gap: 1.5,
          }}
        >
          {[ALL_CARD_DEF, ...STATUS_CARD_DEFS].map((card) => {
            const isAll = card.key === "All";
            return (
              <StatCard
                activeBorder
                key={card.key}
                icon={card.icon}
                title={card.label}
                value={isAll ? filteredRows.length : countByStatus[card.key] || 0}
                accent={card.accent}
                tint={card.tint}
                isActive={isAll ? !statusFilter : statusFilter === card.key}
                onClick={() => {
                  setStatusFilter(isAll ? null : card.key);
                  setPage(0);
                }}
              />
            );
          })}
        </Mui.Box>

        <Mui.Box
          ref={customFilterRef}
          sx={{ ...designShell.sectionCard, ...sharedStickyCustomFilterBarSx }}
        >
          <CustomFilter
            title="Records"
            titleOverride={titleOverride}
            filterHeaders={visibleHeaders}
            data={sites}
            filteredData={rows}
            filteredDataReady={filteredReady}
            onFilteredDataChange={onFilteredDataChange}
            screenAccess={check_access}
            hideCount={true}
            labeledToolbar={true}
            downloadFormats={["excel", "pdf", "csv"]}
            searchPlaceholder="Search site name or address..."
          />
        </Mui.Box>

        <Mui.Box sx={{ ...designShell.sectionCard, width: "100%" }}>
          {isLoading ? (
            <Mui.Box sx={{ py: 6 }}>
              <Loader info="Loading..." />
            </Mui.Box>
          ) : (
            <>
              <Mui.TableContainer
                sx={{
                  width: "100%",
                  // Visible, not auto: the sticky header needs the page as
                  // its scroll parent, and the fixed-layout table always fits.
                  overflowX: "visible",
                  pb: footerHeight ? footerHeight + "px" : 0,
                }}
              >
                <ApproveTable
                  stickyHeader
                  aria-label="Geo-fenced sites"
                  sx={{ width: "100%", tableLayout: "fixed" }}
                >
                  <Mui.TableHead sx={designShell.tableStickyHead}>
                    <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                      {visibleHeaders.map((header) => (
                        <Mui.TableCell
                          key={header.key}
                          scope="col"
                          title={header.name}
                          sx={{
                            ...monitoringHeadCellSx(customFilterHeight),
                            width: balancedColumnWidth(header.key, visibleHeaders, siteHeaders, columnWidths),
                          }}
                        >
                          {header.name}
                        </Mui.TableCell>
                      ))}
                    </Mui.TableRow>
                  </Mui.TableHead>
                  <Mui.TableBody>
                    {rows.length === 0 ? (
                      <TableEmptyState
                        dataLength={sites}
                        colSpan={visibleHeaders.length}
                      />
                    ) : (
                      rows
                        .slice(pageStart, pageStart + rowsPerPage)
                        .map((site) => (
                          <Mui.TableRow
                            key={site.id}
                            onClick={() => setViewId(site.id)}
                            sx={monitoringRowSx(viewId === site.id)}
                          >
                            {visibleHeaders.map((header) => (
                              <Mui.TableCell
                                key={header.key}
                                title={
                                  typeof site[header.key] === "string"
                                    ? site[header.key]
                                    : undefined
                                }
                                sx={monitoringBodyCellSx}
                              >
                                {renderCell(header.key, site)}
                              </Mui.TableCell>
                            ))}
                          </Mui.TableRow>
                        ))
                    )}
                  </Mui.TableBody>
                </ApproveTable>
              </Mui.TableContainer>
              <Mui.Box ref={footerRef} sx={sharedStickyFooterBarSx}>
                {paginationFooter({
                  page: safePage,
                  rowsPerPage,
                  total: rows.length,
                  itemLabel: "sites",
                  rowsPerPageOptions,
                  onPageChange: setPage,
                  onRowsPerPageChange: (event) => {
                    setRowsPerPage(Number(event.target.value));
                    setPage(0);
                  },
                })}
              </Mui.Box>
            </>
          )}
        </Mui.Box>
      </FilterProvider>

      <GlobalSidePanel
        open={Boolean(viewing)}
        onClose={() => setViewId(null)}
        header={
          viewing && (
            <PanelIconHeader
              icon={<PlaceRounded />}
              title={viewing.location_name}
              subtitle="Geo-fenced site details and actions"
              trailing={
                <RequestStatusChip
                  status={viewing.location_status}
                  colorCode={STATUS_COLORS[viewing.location_status] || NEUTRAL_COLOR}
                  sx={{ flexShrink: 0 }}
                />
              }
            />
          )
        }
        bodySx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}
        headerActions={
          viewing && check_access && (
            <Mui.Tooltip title="Edit" arrow slotProps={tooltipStyleProps}>
              <Mui.IconButton
                size="small"
                aria-label={"Edit " + viewing.location_name}
                onClick={() => runAction(viewing, "Edit")}
                sx={{
                  ...designShell.panelSoftIconButton,
                  color: designColors.slate500,
                }}
              >
                <EditOutlined fontSize="small" />
              </Mui.IconButton>
            </Mui.Tooltip>
          )
        }
        footer={
          <Mui.Stack direction="row" justifyContent="flex-end">
            <SecondaryButton onClick={() => setViewId(null)}>Close</SecondaryButton>
          </Mui.Stack>
        }
      >
        {viewing && (
          <Mui.Stack spacing={2}>
            <Mui.Box>
              <Mui.Typography sx={detailSectionLabelSx}>Basic Details</Mui.Typography>
              <Mui.Box sx={detailCardSx}>
                {viewFields.map((field, index) => (
                  <RequestDetailField
                    key={field.label}
                    {...field}
                    isLast={index === viewFields.length - 1}
                  />
                ))}
              </Mui.Box>
            </Mui.Box>

            <PanelSectionCard icon={<PlaceRounded />} title="Location on Map">
              <SiteMap site={viewing} />
            </PanelSectionCard>

            {viewActions.length > 0 && (
              <PanelSectionCard
                icon={<SettingsRounded />}
                title="Actions"
                accent={{ color: designColors.slate600, bg: designColors.slateBg }}
              >
                <Mui.Stack spacing={1} sx={{ py: 1.5 }}>
                  {viewActions.map((option) => {
                    const { label, icon: ActionIcon, danger } = ACTION_META[option];
                    return (
                      <Mui.Button
                        key={option}
                        fullWidth
                        variant="outlined"
                        disabled={Boolean(busyAction)}
                        startIcon={
                          busyAction === option ? (
                            <Mui.CircularProgress size={16} color="inherit" />
                          ) : (
                            <ActionIcon fontSize="small" />
                          )
                        }
                        onClick={() => runAction(viewing, option)}
                        sx={actionButtonSx(danger)}
                      >
                        {label}
                      </Mui.Button>
                    );
                  })}
                </Mui.Stack>
              </PanelSectionCard>
            )}
          </Mui.Stack>
        )}
      </GlobalSidePanel>

      <SiteEditPanel
        open={Boolean(editSite)}
        site={editSite}
        onClose={() => setEditSite(null)}
        onSaved={() => setUpdate(true)}
      />

      <GlobalConfirmDialog
        open={Boolean(siteToDelete)}
        title="Delete site"
        onCancel={() => setSiteToDelete(null)}
        onConfirm={deleteLocation}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        loading={busyAction === "Delete"}
        loadingLabel="Deleting..."
        danger
      >
        Delete {siteToDelete?.location_name} from the geo-fenced sites?
      </GlobalConfirmDialog>
    </>
  );
};
